import { format, isValid, parseISO } from "date-fns";
import { getLeaveDuration } from "../services/leaveService";

export function parseLeaveDate(value) {
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return isValid(date) ? date : null;
    }

    const isoDate = parseISO(trimmed);
    return isValid(isoDate) ? isoDate : null;
  }

  const date = new Date(value);
  return isValid(date) ? date : null;
}

export function formatLeaveDate(value) {
  const date =
    value instanceof Date ? (isValid(value) ? value : null) : parseLeaveDate(value);
  return date ? format(date, "dd MMM yyyy") : "—";
}

export function formatLeaveDateRange(fromDate, toDate) {
  const from = formatLeaveDate(fromDate);
  const to = formatLeaveDate(toDate);
  if (from === "—") return "—";
  if (to === "—" || from === to) return from;
  return `${from} – ${to}`;
}

export function normalizeStatus(status) {
  if (!status) return "Pending";
  const value = String(status);
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function extractLeaveRecords(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.content)) return response.data.content;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.content)) return response.content;
  return [];
}

function normalizeHalfFlag(value) {
  if (value == null) return "N";
  if (typeof value === "boolean") return value ? "Y" : "N";
  const normalized = String(value).trim().toUpperCase();
  if (normalized === "Y" || normalized === "YES" || normalized === "TRUE" || normalized === "1") {
    return "Y";
  }
  return "N";
}

function normalizeLeavesDetail(leavesDetail = []) {
  return leavesDetail.map((day) => ({
    ...day,
    firstHalf: normalizeHalfFlag(day.firstHalf),
    secondHalf: normalizeHalfFlag(day.secondHalf),
  }));
}

function getFromSessionFromDetail(leavesDetail) {
  if (!leavesDetail || leavesDetail.length === 0) return "—";
  const first = leavesDetail[0];
  return first.firstHalf === "Y" ? "First Half" : "Second Half";
}

function getToSessionFromDetail(leavesDetail) {
  if (!leavesDetail || leavesDetail.length === 0) return "—";
  const last = leavesDetail[leavesDetail.length - 1];
  return last.secondHalf === "Y" ? "Second Half" : "First Half";
}

/**
 * Some API responses carry per-record `firstHalf`/`secondHalf` flags instead of
 * a `leavesDetail` array. Normalize them into a detail array so session labels
 * can be derived the same way in both cases.
 */
export function getEffectiveLeavesDetail(record) {
  if (Array.isArray(record?.leavesDetail) && record.leavesDetail.length > 0) {
    return normalizeLeavesDetail(record.leavesDetail);
  }

  const hasSessionFlags = record?.firstHalf != null || record?.secondHalf != null;
  if (!hasSessionFlags) return [];

  const firstHalf = normalizeHalfFlag(record.firstHalf);
  const secondHalf = normalizeHalfFlag(record.secondHalf);

  if (record.startDate && record.endDate && record.startDate !== record.endDate) {
    return normalizeLeavesDetail([
      { date: record.startDate, firstHalf, secondHalf: "Y" },
      { date: record.endDate, firstHalf: "Y", secondHalf },
    ]);
  }

  return normalizeLeavesDetail([{ date: record.startDate, firstHalf, secondHalf }]);
}

export function getLeaveSessionFields(record) {
  const sessionDetail = getEffectiveLeavesDetail(record);
  const fromSession = getFromSessionFromDetail(sessionDetail);
  const toSession = getToSessionFromDetail(sessionDetail);

  return {
    fromSession,
    toSession,
    session: formatLeaveSession(sessionDetail, fromSession, toSession),
  };
}

export function getLeaveSessionLabel(record) {
  return getLeaveSessionFields(record).session;
}

export function formatLeaveSession(leavesDetail = [], fromSession, toSession) {
  if (leavesDetail.length === 1) {
    const day = leavesDetail[0];
    const hasFirst = day.firstHalf === "Y";
    const hasSecond = day.secondHalf === "Y";
    if (hasFirst && hasSecond) return "Full Day";
    if (hasFirst) return "First Half";
    if (hasSecond) return "Second Half";
    return "—";
  }

  if (leavesDetail.length > 1) {
    const allFullDays = leavesDetail.every(
      (day) => day.firstHalf === "Y" && day.secondHalf === "Y"
    );
    if (allFullDays) return "Full Day";

    const from = fromSession || getFromSessionFromDetail(leavesDetail);
    const to = toSession || getToSessionFromDetail(leavesDetail);
    if (from === "First Half" && to === "Second Half") return "Full Day";
    if (from === to) return from;
    return `${from} – ${to}`;
  }

  const from = fromSession || "First Half";
  const to = toSession || "Second Half";
  if (from === "First Half" && to === "Second Half") return "Full Day";
  if (from === to) return from;
  return `${from} – ${to}`;
}

export function getLeaveRecordEmpId(record) {
  if (!record || typeof record !== "object") return undefined;

  const candidates = [
    record.empId,
    record.employeeId,
    record.emp_id,
    record.employee_id,
    record.employee?.empId,
    record.employee?.id,
    record.employee?.employeeId,
  ];

  for (const value of candidates) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }

  return undefined;
}

export function mapLeaveRecord(record, leaveTypes = []) {
  const fromDate = parseLeaveDate(record.startDate);
  const toDate = parseLeaveDate(record.endDate);
  const appliedDate = parseLeaveDate(record.createdAt || record.createdOn) || fromDate;
  const leavesDetail = record.leavesDetail || [];
  // Session labels fall back to record-level firstHalf/secondHalf flags when
  // the API response has no leavesDetail array.
  const sessionDetail = getEffectiveLeavesDetail(record);
  const { fromSession, toSession, session } = getLeaveSessionFields(record);
  const leaveTypeId = record.leaveTypeId;
  const matchedType = leaveTypes.find((type) => type.typeId === leaveTypeId);

  return {
    id: String(record.leaveId),
    leaveId: record.leaveId,
    empId: getLeaveRecordEmpId(record),
    leaveTypeId,
    type: record.leaveTypeName || matchedType?.leaveName || "Leave",
    from: formatLeaveDate(fromDate),
    to: formatLeaveDate(toDate),
    dateRange: formatLeaveDateRange(fromDate, toDate),
    fromDate: record.startDate,
    toDate: record.endDate,
    days: getLeaveDuration(record),
    appliedOn: formatLeaveDate(appliedDate),
    appliedOnTime: appliedDate?.getTime() ?? 0,
    status: normalizeStatus(record.leaveStatus || record.status),
    reason: record.leaveReason || record.reason,
    rejectionReason: record.rejectionReason,
    actionByRemarks: record.actionByRemarks,
    leavesDetail: sessionDetail.length > 0 ? sessionDetail : leavesDetail,
    fromSession,
    toSession,
    session,
  };
}

export function mapLeaveRecords(records, leaveTypes = []) {
  return records.map((record) => mapLeaveRecord(record, leaveTypes));
}
